import { makeAutoObservable, ObservableMap } from "mobx";
import { Channel, Message, Server, User } from "revolt.js";
import { decodeTime } from "ulid";

import { getDisplayName } from "../../lib/displayName";
import {
    getAttachmentFallback,
    getSystemMessageBody,
} from "../../lib/notificationBody";
import { prepareTextForNotification } from "../../lib/prepareTextForNotification";

import { history, routeInformation } from "../../context/history";

import { getClientController } from "../../controllers/client/ClientController";
import State from "../State";

/**
 * Possible notification states.
 * TODO: make "muted" gray out the channel
 * TODO: add server defaults
 */
export type NotificationState = "all" | "mention" | "none" | "muted";

/**
 * Default notification states for various types of channels.
 */
export const DEFAULT_STATES: {
    [key in Channel["channel_type"]]: NotificationState;
} = {
    SavedMessages: "all",
    DirectMessage: "all",
    Group: "all",
    TextChannel: undefined!,
    VoiceChannel: undefined!,
};

/**
 * Default state for servers.
 */
export const DEFAULT_SERVER_STATE: NotificationState = "mention";

/**
 * Create a notification either directly or using service worker.
 * @param title Notification Title
 * @param options Notification Options
 * @returns Notification
 */
async function createNotification(
    title: string,
    options: globalThis.NotificationOptions,
) {
    try {
        return new Notification(title, options);
    } catch (err) {
        const sw = await navigator.serviceWorker.getRegistration();
        sw?.showNotification(title, options);
    }
}

/**
 * Manages the user's notification preferences.
 */
export default class NotificationOptions {
    private state: State;
    private activeNotifications: Record<string, Notification>;
    /**
     * Closure capturing user ID at registration time.
     * Set by State.registerListeners(); null until client is available.
     */
    private onChannelAckListener: ((packet: any) => void) | null;

    private server: ObservableMap<string, NotificationState>;
    private channel: ObservableMap<string, NotificationState>;

    /**
     * Construct new Experiments store.
     */
    constructor(state: State) {
        this.server = new ObservableMap();
        this.channel = new ObservableMap();

        makeAutoObservable(this, {
            onMessage: false,
            onRelationship: false,
            onVisibilityChange: false,
        });

        this.state = state;
        this.activeNotifications = {};

        this.onMessage = this.onMessage.bind(this);
        this.onRelationship = this.onRelationship.bind(this);
        this.onVisibilityChange = this.onVisibilityChange.bind(this);
        this.onChannelAckListener = null;
    }

    /**
     * Compute the actual notification state for a given Channel.
     * @param channel Channel
     * @returns Notification state
     */
    computeForChannel(channel: Channel) {
        if (this.channel.has(channel._id)) {
            return this.channel.get(channel._id);
        }

        if (channel.server_id) {
            return this.computeForServer(channel.server_id);
        }

        return DEFAULT_STATES[channel.channel_type];
    }

    /**
     * Check whether an incoming message should notify the user.
     * @param message Message
     * @returns Whether it should notify the user
     */
    shouldNotify(message: Message) {
        // Make sure the author is not blocked.
        if (message.author?.relationship === "Blocked") {
            return false;
        }

        // Check if the message was sent by us.
        const user = message.client.user!;
        if (message.author_id === user._id) {
            return false;
        }

        // Check whether we are busy.
        if (user.status?.presence === "Busy") {
            return false;
        }

        // Check channel notification settings
        const mentioned = message.isMentioned();
        switch (this.computeForChannel(message.channel!)) {
            case "muted":
            case "none":
                // Ignore if muted.
                return false;
            case "mention":
                // Ignore if it doesn't mention us.
                if (!mentioned) return false;
        }

        // Check if we are in focus mode
        if (user.status?.presence === "Focus" && !mentioned) {
            return false;
        }

        return true;
    }

    /**
     * Compute the notification state for a given server.
     * @param server_id Server ID
     * @returns Notification state
     */
    computeForServer(server_id: string) {
        if (this.server.has(server_id)) {
            return this.server.get(server_id);
        }

        return DEFAULT_SERVER_STATE;
    }

    /**
     * Get the notification state of a channel.
     * @param channel_id Channel ID
     * @returns Notification state
     */
    getChannelState(channel_id: string) {
        return this.channel.get(channel_id);
    }

    /**
     * Set the notification state of a channel.
     * @param channel_id Channel ID
     * @param state Notification state
     */
    async setChannelState(channel_id: string, state?: NotificationState) {
        const client = getClientController().readyClient;
        if (!client) return;

        const channel = client.channels.get(channel_id);
        const server_id = channel?.server_id;

        const previous = this.channel.get(channel_id);
        if (state) {
            this.channel.set(channel_id, state);
            try {
                await client.api.post("/settings/notifications/", {
                    server_id,
                    channel_id,
                    state,
                });
            } catch (err) {
                console.error("Failed to set channel notification state:", err);
                this.channel.delete(channel_id);
                if (previous !== undefined) {
                    this.channel.set(channel_id, previous);
                }
            }
        } else {
            this.channel.delete(channel_id);
            try {
                await client.api.delete("/settings/notifications/", {
                    server_id,
                    channel_id,
                });
            } catch (err) {
                console.error(
                    "Failed to delete channel notification state:",
                    err,
                );
                if (previous !== undefined) {
                    this.channel.set(channel_id, previous);
                }
            }
        }
    }

    /**
     * Get the notification state of a server.
     * @param server_id Server ID
     * @returns Notification state
     */
    getServerState(server_id: string) {
        return this.server.get(server_id);
    }

    /**
     * Set the notification state of a server.
     * @param server_id Server ID
     * @param state Notification state
     */
    async setServerState(server_id: string, state?: NotificationState) {
        const client = getClientController().readyClient;
        if (!client) return;

        const previous = this.server.get(server_id);
        if (state) {
            this.server.set(server_id, state);
            try {
                await client.api.post("/settings/notifications/", {
                    server_id,
                    state,
                });
            } catch (err) {
                console.error("Failed to set server notification state:", err);
                this.server.delete(server_id);
                if (previous !== undefined) {
                    this.server.set(server_id, previous);
                }
            }
        } else {
            this.server.delete(server_id);
            try {
                await client.api.delete("/settings/notifications/", {
                    server_id,
                });
            } catch (err) {
                console.error(
                    "Failed to delete server notification state:",
                    err,
                );
                if (previous !== undefined) {
                    this.server.set(server_id, previous);
                }
            }
        }
    }

    /**
     * Load all notification settings from server.
     */
    async loadFromServer() {
        const client = getClientController().readyClient;
        if (!client) return;

        try {
            const data = await client.api.get("/settings/notifications/", {});
            const settings = Array.isArray(data) ? data : [];

            this.server.clear();
            this.channel.clear();

            for (const s of settings) {
                if (s.channel_id) {
                    this.channel.set(s.channel_id, s.state);
                } else if (s.server_id) {
                    this.server.set(s.server_id, s.state);
                }
            }
        } catch (err) {
            console.error("Failed to load notification settings:", err);
        }
    }

    /**
     * Migrate old localforage notification data to server.
     * Only clears localforage if ALL entries migrated successfully.
     */
    async migrate() {
        const client = getClientController().readyClient;
        if (!client) return;

        const localforage = (await import("localforage")).default;
        const data = (await localforage.getItem("notifications")) as {
            server?: Record<string, NotificationState>;
            channel?: Record<string, NotificationState>;
        } | null;

        if (!data || typeof data !== "object") {
            return;
        }

        let failed = false;

        try {
            // Migrate server settings
            if (data.server && typeof data.server === "object") {
                for (const [server_id, state] of Object.entries(data.server)) {
                    try {
                        await client.api.post("/settings/notifications/", {
                            server_id,
                            state: state as NotificationState,
                        });
                    } catch (err) {
                        console.error(
                            `Failed to migrate server ${server_id}:`,
                            err,
                        );
                        failed = true;
                    }
                }
            }

            // Migrate channel settings
            if (data.channel && typeof data.channel === "object") {
                for (const [channel_id, state] of Object.entries(
                    data.channel,
                )) {
                    const channel = client.channels.get(channel_id);
                    if (!channel) {
                        console.warn(
                            `Channel ${channel_id} not loaded yet, skipping migration entry`,
                        );
                        continue;
                    }

                    const server_id = channel.server_id;
                    try {
                        await client.api.post("/settings/notifications/", {
                            ...(server_id && { server_id }),
                            channel_id,
                            state: state as NotificationState,
                        });
                    } catch (err) {
                        console.error(
                            `Failed to migrate channel ${channel_id}:`,
                            err,
                        );
                        failed = true;
                    }
                }
            }

            if (!failed) {
                await localforage.removeItem("notifications");
                console.log("Notification settings migrated successfully");
            }
        } catch (err) {
            console.error("Migration failed:", err);
        }
    }

    /**
     * Check whether a Channel or Server is muted.
     * @param target Channel or Server
     * @returns Whether this object is muted
     */
    isMuted(target?: Channel | Server) {
        let value: NotificationState | undefined;
        if (target instanceof Channel) {
            value = this.computeForChannel(target);
        } else if (target instanceof Server) {
            value = this.computeForServer(target._id);
        }

        if (value === "muted") {
            return true;
        }

        return false;
    }

    /**
     * Handle incoming messages and create a notification.
     * @param message Message
     */
    async onMessage(message: Message) {
        // Ignore if we are currently looking and focused on the channel.
        if (
            message.channel_id === routeInformation.getChannel() &&
            document.hasFocus()
        )
            return;

        // Ignore if muted.
        if (!this.shouldNotify(message)) return;

        // Play a sound and skip notif if disabled.
        this.state.settings.sounds.playSound("message");
        if (!this.state.settings.get("notifications:desktop")) return;

        const effectiveName =
            message.masquerade?.name ?? message.author?.username;

        let title;
        switch (message.channel?.channel_type) {
            case "SavedMessages":
                return;
            case "DirectMessage":
                title = `@${effectiveName}`;
                break;
            case "Group":
                if (message.author?._id === "00000000000000000000000000") {
                    title = message.channel.name;
                } else {
                    title = `@${effectiveName} - ${message.channel.name}`;
                }
                break;
            case "TextChannel":
                title = `@${effectiveName} (#${message.channel.name}, ${message.channel.server?.name})`;
                break;
            default:
                title = message.channel?._id;
                break;
        }

        let image;
        if (message.attachments) {
            const imageAttachment = message.attachments.find(
                (x) => x.metadata.type === "Image",
            );
            if (imageAttachment) {
                image = message.client.generateFileURL(imageAttachment, {
                    max_side: 720,
                });
            }
        }

        let body, icon;
        if (message.content) {
            body = prepareTextForNotification(message);

            if (message.masquerade?.avatar) {
                icon = message.client.proxyFile(message.masquerade.avatar);
            } else {
                icon = message.author?.generateAvatarURL({ max_side: 256 });
            }
        } else if (message.system) {
            const users = message.client.users;
            const user =
                "id" in message.system
                    ? users.get(message.system.id)
                    : undefined;

            // ! FIXME: I've had to strip translations while
            // ! I move stuff into the new project structure
            // RU body text lives in lib/notificationBody.ts (mirrors the BE FCM/APN table).
            body = getSystemMessageBody(
                message.system,
                user ? getDisplayName(user) : undefined,
            );
            icon = user?.generateAvatarURL({ max_side: 256 });
        }

        // Fallback body for messages without text (file/image only)
        if (!body) {
            body = getAttachmentFallback(message.attachments);
        }

        const notif = await createNotification(title!, {
            icon,
            image,
            body,
            timestamp: decodeTime(message._id),
            tag: message.channel?._id,
            badge: "/assets/icons/android-chrome-512x512.png",
            silent: true,
        });

        if (notif) {
            notif.addEventListener("click", () => {
                window.focus();
                if (window.isNative && window.native.focusWindow) {
                    window.native.focusWindow();
                }

                const id = message.channel_id;
                if (id !== routeInformation.getChannel()) {
                    const channel = message.client.channels.get(id);
                    if (channel) {
                        if (channel.channel_type === "TextChannel") {
                            history.push(
                                `/server/${channel.server_id}/channel/${id}`,
                            );
                        } else {
                            history.push(`/channel/${id}`);
                        }
                    }
                }
            });

            this.activeNotifications[message.channel_id] = notif;

            notif.addEventListener("close", () => {
                delete this.activeNotifications[message.channel_id];
            });
        }
    }

    /**
     * Handle user relationship changes.
     * @param user User relationship changed with
     */
    async onRelationship(user: User) {
        // Ignore if disabled.
        if (!this.state.settings.get("notifications:desktop")) return;

        // Check whether we are busy.
        // This is checked by `shouldNotify` in the case of messages.
        if (user.status?.presence === "Busy") {
            return false;
        }

        let event;
        switch (user.relationship) {
            case "Incoming":
                event = `${getDisplayName(user)} отправил вам запрос дружбы`;
                break;
            case "Friend":
                event = `Теперь вы дружите с ${getDisplayName(user)}`;
                break;
            default:
                return;
        }

        const notif = await createNotification(event, {
            icon: user.generateAvatarURL({ max_side: 256 }),
            badge: "/assets/icons/android-chrome-512x512.png",
            timestamp: +new Date(),
        });

        notif?.addEventListener("click", () => {
            history.push(`/friends`);
        });
    }

    /**
     * Called when document visibility changes.
     */
    onVisibilityChange() {
        if (document.visibilityState === "visible") {
            const channel_id = routeInformation.getChannel()!;
            if (this.activeNotifications[channel_id]) {
                this.activeNotifications[channel_id].close();
            }
        }
    }

    /**
     * Close notification when user read the channel on another device.
     */
    private onChannelAck = (channel_id: string) => {
        const notif = this.activeNotifications[channel_id];
        if (notif) notif.close();
    };
}
